import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

const steps = [
  { icon: 'ri-phone-line', title: 'Initial Consultation', desc: 'Call us at (209) 451-2288 to discuss your reconstruction needs. We assess the scope and create a detailed project plan.' },
  { icon: 'ri-search-eye-line', title: 'Damage Assessment', desc: 'Our team performs a thorough structural evaluation to identify all areas requiring repair or full reconstruction.' },
  { icon: 'ri-file-list-3-line', title: 'Project Planning', desc: 'We develop a comprehensive reconstruction plan with timelines, materials, and cost estimates for your approval.' },
  { icon: 'ri-tools-line', title: 'Structural Repairs', desc: 'Framing, subfloor, roofing, and structural components are repaired or replaced to code.' },
  { icon: 'ri-paint-brush-line', title: 'Interior Finishing', desc: 'Drywall, flooring, cabinetry, painting, and all interior finishes are completed to match your original home.' },
  { icon: 'ri-home-smile-line', title: 'Final Walkthrough', desc: 'We conduct a detailed final inspection with you to ensure every detail meets your expectations before handover.' },
];

const reasons = [
  { icon: 'ri-award-line', title: 'Licensed Contractor', desc: 'Fully licensed and insured general contractor with years of reconstruction experience in Merced, CA.' },
  { icon: 'ri-shield-check-line', title: 'Code-Compliant Work', desc: 'All reconstruction work meets California building codes and passes local inspections.' },
  { icon: 'ri-file-list-3-line', title: 'Insurance Coordination', desc: 'We work directly with your insurance company to ensure full coverage and a smooth claims process.' },
  { icon: 'ri-tools-line', title: 'One-Stop Solution', desc: 'From mitigation to full rebuild — no need to hire multiple contractors. We handle everything.' },
  { icon: 'ri-map-pin-line', title: 'Local Merced Experts', desc: 'We know Merced\'s building requirements and have strong relationships with local suppliers and inspectors.' },
  { icon: 'ri-thumb-up-line', title: 'Quality Craftsmanship', desc: 'We take pride in delivering high-quality finishes that restore your property to its original beauty.' },
];

export default function ReconstructionPage() {
  useEffect(() => {
    const siteUrl = 'https://mercedwaterrestoration.com';

    document.title = 'Reconstruction Services Merced CA | Full Rebuild After Damage | Indoor-Restore';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Full interior & structural reconstruction after water, fire & storm damage in Merced, CA. Licensed contractor for drywall, flooring, roofing & complete rebuilds. Call (209) 451-2288.');

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'reconstruction services Merced CA, home rebuild after water damage, fire damage reconstruction, storm damage rebuild Central Valley, licensed contractor Merced');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/services/reconstruction`);

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Reconstruction Services",
      "name": "Reconstruction Services in Merced, CA",
      "description": "Full interior and structural reconstruction after water, fire, and storm damage. Licensed general contractor providing drywall, flooring, painting, and complete rebuild services in Merced County.",
      "url": `${siteUrl}/services/reconstruction`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Indoor-Restore Services",
        "telephone": "+12094512288",
        "email": "info@mercedwaterrestoration.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1729 U St",
          "addressLocality": "Merced",
          "addressRegion": "CA",
          "postalCode": "95340",
          "addressCountry": "US"
        }
      },
      "areaServed": {
        "@type": "City",
        "name": "Merced",
        "containedInPlace": { "@type": "State", "name": "California" }
      },
      "availableChannel": {
        "@type": "ServiceChannel",
        "servicePhone": {
          "@type": "ContactPoint",
          "telephone": "+12094512288",
          "contactType": "Customer Service",
          "availableLanguage": "English"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/adb8e2eefea4455d1d151bb4687717d5.jpg"
            alt="Reconstruction Services Merced CA"
            className="w-full h-full object-cover object-top"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/65"></div>
        </div>
        <div className="relative z-10 text-center px-4 w-full max-w-5xl mx-auto py-24">
          <span className="inline-block bg-teal-500/30 border border-teal-400/50 text-teal-200 text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
            Full-Service Reconstruction
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-5 leading-tight">
            Reconstruction Services<br />in Merced, CA
          </h1>
          <p className="text-xl text-white/85 mb-8 max-w-2xl mx-auto">
            From structural repairs to complete rebuilds — Indoor-Restore Services restores your property to its original condition after any disaster.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:2094512288" className="bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i>Call (209) 451-2288
            </a>
            <a href="/#contact" className="bg-white text-teal-700 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              Get Free Estimate
            </a>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-5">Complete Rebuild After Water, Fire & Storm Damage</h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                After a disaster, the cleanup is only half the job. Once mitigation is complete, your property needs to be rebuilt — and that's where <strong>Indoor-Restore Services</strong> truly sets itself apart.
              </p>
              <p className="text-gray-600 leading-relaxed mb-5">
                As a licensed general contractor in Merced, CA, we handle the full reconstruction phase in-house. No subcontracting delays, no coordination headaches — just one trusted team taking your property from damaged to fully restored.
              </p>
              <ul className="space-y-3">
                {[
                  'Structural framing & subfloor repair',
                  'Drywall installation & finishing',
                  'Flooring replacement (hardwood, tile, carpet)',
                  'Roof repair & replacement',
                  'Kitchen & bathroom reconstruction',
                  'Interior painting & trim work',
                ].map(item => (
                  <li key={item} className="flex items-center gap-3 text-gray-700 text-sm">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl h-96">
              <img
                src="https://readdy.ai/api/search-image?query=skilled%20contractor%20installing%20new%20drywall%20and%20flooring%20inside%20a%20beautifully%20reconstructed%20California%20home%20interior%20after%20water%20damage%20restoration%20with%20fresh%20paint%20and%20modern%20finishes%20bright%20natural%20lighting%20clean%20professional%20scene&width=800&height=600&seq=recon-overview-02&orientation=landscape"
                alt="Interior reconstruction and finishing work"
                className="w-full h-full object-cover object-top"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Reconstruction Process</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">A structured, transparent process that keeps you informed at every stage of your property's rebuild.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="bg-white rounded-xl p-7 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${step.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <span className="text-3xl font-bold text-teal-100">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-4">Why Choose Indoor-Restore Services?</h2>
            <p className="text-white/80 max-w-2xl mx-auto">Merced's trusted reconstruction contractor — quality craftsmanship, code-compliant work, and seamless insurance coordination.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {reasons.map((r, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-7 border border-white/20 hover:bg-white/20 transition-all">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-5">
                  <i className={`${r.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{r.title}</h3>
                <p className="text-white/75 text-sm leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Ready to Rebuild? Let's Talk.</h2>
          <p className="text-gray-500 mb-8 text-lg">Contact Indoor-Restore Services today for a free reconstruction estimate. We serve Merced, CA and the entire Central Valley.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:2094512288" className="bg-teal-600 text-white px-10 py-4 rounded-lg text-lg font-bold hover:bg-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i>(209) 451-2288
            </a>
            <a href="mailto:info@mercedwaterrestoration.com" className="border-2 border-teal-600 text-teal-600 px-10 py-4 rounded-lg text-lg font-bold hover:bg-teal-50 transition-all whitespace-nowrap cursor-pointer">
              Email Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
